import { Box, Icon, Paragraph } from '@zillow/constellation-10';
import { IconDeleteOutline } from '@zillow/constellation-icons';
import { useMemo } from 'react';
import type { MessageList_GetConversationQuery } from '../../types/GraphqlTypes';
import StringConstants from '../../constants/StringConstants';
import ConditionalWrapper from '../ConditionalWrapper';
import type { HandleOpenDeleteAttachmentsModal, HandleOpenImagesViewer } from '../MessageList';
import DeleteAttachmentsButton from './components/DeleteAttachmentsButton';
import ImageThumbnailGrid from './components/ImageThumbnailGrid';
import NonImageList from './components/NonImageList';
import { logError } from '@zg-rentals/log-error';
import { getCurrentSessionURL } from '../../utils/fullStory';

export type AttachmentsForMessage = ArrayElement<
  MessageList_GetConversationQuery['ZRM_getConversationById']['conversation']
>['attachments'];

type Props = {
  attachments: AttachmentsForMessage;
  isMessageOwner: boolean;
  isMessageSent: boolean;
  messageId: string;
  openDeleteAttachmentsModal: HandleOpenDeleteAttachmentsModal;
  openImagesViewer: HandleOpenImagesViewer;
};

const { IMAGE_FILE_TYPE } = StringConstants.ATTACHMENTS;
const MAX_THUMBNAIL_COUNT = 6;

export const filterAttachments = (attachments: AttachmentsForMessage) => {
  const imageAttachments: AttachmentsForMessage = [];
  const nonImageAttachments: AttachmentsForMessage = [];
  let hasSomeDeletedAttachments = false;
  let hasEveryAttachmentExpired = true;

  attachments.forEach((attachment) => {
    const { fileName = '', fileType = '', isAttachmentDeleted, isAttachmentExpired } = attachment;

    if (isAttachmentDeleted) {
      hasSomeDeletedAttachments = true;
      return;
    }

    if (!isAttachmentExpired) {
      hasEveryAttachmentExpired = false;
    }

    if ((fileName as string) && fileName?.includes('unknown')) return;

    if ((fileType as string) && fileType?.includes(IMAGE_FILE_TYPE)) {
      return imageAttachments.push(attachment);
    }

    return nonImageAttachments.push(attachment);
  });

  return {
    imageAttachments,
    nonImageAttachments,
    hasEveryAttachmentExpired,
    hasSomeDeletedAttachments,
  };
};

type AttachmentsRenderError = {
  conversationId: string;
  loggerName: string;
};

export const logAttachmentsRenderError = ({ conversationId, loggerName }: AttachmentsRenderError) => {
  logError({
    error: new Error('logAttachmentsRenderError'),
    context: {
      fullStorySessionUrl: getCurrentSessionURL(),
      details: `${loggerName} Error rendering attachment, attachmentMetadataStatus is not complete or url is missing`,
      conversationId,
    },
  });
};

const MessageAttachments = ({
  attachments,
  isMessageOwner,
  isMessageSent,
  messageId,
  openDeleteAttachmentsModal,
  openImagesViewer,
}: Props) => {
  const { imageAttachments, nonImageAttachments, hasEveryAttachmentExpired, hasSomeDeletedAttachments } = useMemo(
    () => filterAttachments(attachments),
    [attachments],
  );

  const isDeleteIconDisabled = !isMessageSent || hasEveryAttachmentExpired;
  const hasImageAttachments = imageAttachments.length > 0;
  const hasNonImageAttachments = nonImageAttachments.length > 0;
  const hasAllDeletedAttachments = !hasImageAttachments && !hasNonImageAttachments;
  const shouldShowDeleteIcon = (isMessageOwner && !hasAllDeletedAttachments && !hasEveryAttachmentExpired) || false;

  // Create a mutated array to only pass down the max
  // images necessary for rendering in the thumbnail grid
  const imageAttachmentsSubset = [...imageAttachments];
  let overflowImagesCount = 0;
  if (imageAttachments.length > MAX_THUMBNAIL_COUNT) {
    overflowImagesCount = imageAttachments.length - MAX_THUMBNAIL_COUNT;
    imageAttachmentsSubset.length = MAX_THUMBNAIL_COUNT;
  }

  const handleImageThumbnailClick = (zDocId: string) => {
    const selectedImageIndex = imageAttachments.findIndex((imageAttachment) => imageAttachment.zDocId === zDocId);
    openImagesViewer({ imageAttachments, selectedImageIndex });
  };

  const handleOpenDeleteAttachmentsModal = () => {
    openDeleteAttachmentsModal({ messageId, attachments: [...imageAttachments, ...nonImageAttachments] });
  };

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isMessageOwner ? 'flex-end' : 'flex-start',
        width: '100%',
      }}
    >
      {hasSomeDeletedAttachments && (
        <Box
          css={{
            display: 'flex',
            alignItems: 'center',
            paddingBlock: '20px',
            paddingInline: 'default',
            marginBottom: 'tighter',
            border: '1px solid',
            borderColor: isMessageOwner ? 'border.onHero.express.trust.hero' : 'border.muted',
            borderRadius: isMessageOwner ? '24px 24px 0 24px' : '0 24px 24px 24px',
          }}
        >
          <Icon
            css={{ color: 'border.action.neutral.default', fontSize: '1.4em', marginRight: 'default' }}
            render={<IconDeleteOutline />}
          />
          <Paragraph textStyle="body-xs" css={{ color: 'text.subtle', maxWidth: '174px' }}>
            One or more attachments were removed for all parties
          </Paragraph>
        </Box>
      )}

      {hasImageAttachments && (
        <ConditionalWrapper
          shouldRenderWrapperElement={shouldShowDeleteIcon}
          wrapper={(children) => (
            <Box css={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end' }}>{children}</Box>
          )}
        >
          <>
            {shouldShowDeleteIcon && !hasNonImageAttachments && (
              <DeleteAttachmentsButton isDisabled={isDeleteIconDisabled} onClick={handleOpenDeleteAttachmentsModal} />
            )}

            <ImageThumbnailGrid
              imageAttachments={imageAttachmentsSubset}
              isMessageOwner={isMessageOwner}
              onClick={handleImageThumbnailClick}
              overflowCount={overflowImagesCount}
            />
          </>
        </ConditionalWrapper>
      )}

      {hasNonImageAttachments && (
        <ConditionalWrapper
          shouldRenderWrapperElement={shouldShowDeleteIcon}
          wrapper={(children) => (
            <Box css={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end' }}>{children}</Box>
          )}
        >
          <>
            {shouldShowDeleteIcon && (
              <DeleteAttachmentsButton isDisabled={isDeleteIconDisabled} onClick={handleOpenDeleteAttachmentsModal} />
            )}

            <NonImageList isMessageOwner={isMessageOwner} nonImageAttachments={nonImageAttachments} />
          </>
        </ConditionalWrapper>
      )}
    </Box>
  );
};

export default MessageAttachments;
